import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Member } from '@/types';
import { policyNoFromLoanNo } from '@/lib/loans';

/** Every drawer that can be opened from anywhere in the app. */
export type DrawerKind =
  | 'viewMember'
  | 'editMember'
  | 'addPolicy'
  | 'editPolicy'
  | 'policyDetails'
  | 'policyPayment'
  | 'policyLoan'
  | 'policySavings'
  | 'memberBalance'
  | 'memberTransactions';

/** The member a drawer opens for, taken from the clicked row so the header shows at once. */
export type DrawerMember = Pick<Member, 'id' | 'name' | 'memberNo'>;

export interface DrawerTarget {
  kind: DrawerKind;
  member: DrawerMember;
  policyId?: string;
  /** Set by policy / loan number links; the drawer finds the policy in the member's list. */
  policyNo?: string;
}

interface DrawerContextValue {
  target: DrawerTarget | null;
  openDrawer: (kind: DrawerKind, member: DrawerMember, policyId?: string) => void;
  closeDrawer: () => void;
  /** Policy number link → Policy Details with that policy open. */
  openPolicy: (member: DrawerMember, policyNo: string) => void;
  /** Loan number link → the Policy Loan drawer of that loan's policy. */
  openLoan: (member: DrawerMember, loanNo: string) => void;
}

const DrawerContext = createContext<DrawerContextValue | undefined>(undefined);

/**
 * Holds which drawer is open. The drawers themselves render once in the layout (AppDrawers),
 * so a page, a table cell or another drawer can open any of them.
 */
export function DrawerProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<DrawerTarget | null>(null);

  const openDrawer = useCallback((kind: DrawerKind, member: DrawerMember, policyId?: string) => {
    setTarget({ kind, member: { id: member.id, name: member.name, memberNo: member.memberNo }, policyId });
  }, []);

  const closeDrawer = useCallback(() => setTarget(null), []);

  const value = useMemo<DrawerContextValue>(
    () => ({
      target,
      openDrawer,
      closeDrawer,
      openPolicy: (member, policyNo) => setTarget({ kind: 'policyDetails', member, policyNo }),
      openLoan: (member, loanNo) => setTarget({ kind: 'policyLoan', member, policyNo: policyNoFromLoanNo(loanNo) }),
    }),
    [target, openDrawer, closeDrawer]
  );

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

export function useDrawers() {
  const context = useContext(DrawerContext);
  if (!context) throw new Error('useDrawers must be used inside DrawerProvider');
  return context;
}
